import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAuthState, useSignOut } from "react-firebase-hooks/auth";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "firebase/auth";
import { getBusinessVertical, type BusinessVertical } from "@graftus/schema";
import {
  DEFAULT_ASSESSMENT_PROVIDER_ID,
  REQUIRED_ATTESTATIONS,
  TERMINAL_BOARD_STATUSES,
  type AttestationKind,
  type EmployeeImportRow,
  type EmployeeRowErrors,
  type OnboardingDraftPatch,
  type OnboardingDraftStep,
} from "@graftus/schema";
import { hasProductAccess } from "@/utils/productAccess";
import { buildInviteSlots, formatSlotLabel, DEFAULT_TIME_ZONE } from "@/lib/onboarding/slots";
import { useSalesTeams, useSalesTeamsMembers } from "@/hooks/queries/use-sales-teams";
import { useAssessmentInvitesList, useTeamReadiness } from "@/hooks/queries/use-assessment-tests";
import {
  useCreateAssessmentInvites,
  useEnsureProviderCampaign,
} from "@/hooks/mutations/use-assessment-tests";
import { auth } from "@/config/firebaseConfig";
import { useProfile } from "@/context/ProfileContext";
import { trpcClient } from "@/trpc/client";
import {
  setUserId,
  trackBusinessRegistrationCompleted,
  trackBusinessRegistrationError,
  trackOnboardingStarted,
  trackOnboardingStepCompleted,
  trackOnboardingStepViewed,
  trackUserRegistrationCompleted,
} from "@/lib/analytics";
import { track, LogEvents } from "@/analytics/track";
import { useOnboardingStep } from "@/hooks/use-onboarding-step";
import { useOnboardingDraft } from "@/hooks/queries/use-onboarding-draft";
import {
  useAttest,
  useMarkOnboardingComplete,
  useSaveOnboardingDraft,
  useSuggestInviteGreeting,
} from "@/hooks/mutations/use-onboarding-draft";
import { useImportSalesTeamMembers } from "@/hooks/mutations/use-sales-teams";
import { useSectorWords } from "@/hooks/use-sector-words";
import {
  stripEmailLinkParams,
  useEmailLinkAuth,
  type EmailLinkAccount,
} from "@/hooks/use-email-link-auth";
import {
  clearLocalDraft,
  readLocalDraft,
  writeLocalDraft,
  type LocalDraft,
  type LocalDraftOrganisation,
} from "@/lib/onboarding/local-draft";
import { ONBOARDING_STEPS, type OnboardingScreen } from "@/types/onboarding";
import OnboardingShell from "@/components/Onboarding/OnboardingShell";
import type { AutosaveState } from "@/components/Onboarding/AutosaveIndicator";
import { WelcomeScreen } from "@/components/Onboarding/flow/WelcomeScreen";
import { AccountStep } from "@/components/Onboarding/flow/AccountStep";
import { SectorStep } from "@/components/Onboarding/flow/SectorStep";
import {
  OrganisationStep,
  type OrganisationValues,
} from "@/components/Onboarding/flow/OrganisationStep";
import { EmployeesStep, type ImportSummary } from "@/components/Onboarding/flow/EmployeesStep";
import { TeamsStep } from "@/components/Onboarding/flow/TeamsStep";
import { InviteStep, type GreetingSource } from "@/components/Onboarding/flow/InviteStep";
import { InviteWaitingStep } from "@/components/Onboarding/flow/InviteWaitingStep";
import { DoneScreen } from "@/components/Onboarding/flow/DoneScreen";
import { DevThemeSwitch } from "@/components/Development/DevThemeSwitch";

/**
 * The six-step onboarding (GRA-171): account, sector, organisation,
 * employees, teams, invite. The URL holds the screen, the step machine clamps
 * it, the browser holds steps 1-3 until the organisation step creates the
 * business, and the server draft takes over from there.
 */
export default function OnboardingView() {
  const { t, i18n } = useTranslation("onboarding");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, loadingUser] = useAuthState(auth);
  const [signOut] = useSignOut(auth);
  const {
    businessProfile,
    loading: profileLoading,
    refreshProfile,
    setProfileCreated,
  } = useProfile();

  const [localDraft, setLocalDraft] = useState<LocalDraft>(() => readLocalDraft());
  const [localBusinessId, setLocalBusinessId] = useState<string | null>(null);
  const [localDraftStep, setLocalDraftStep] = useState<OnboardingDraftStep | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Steps 4-5: the employees table and the team names, hydrated once from the
  // server draft and saved back with a short debounce.
  const [rows, setRows] = useState<EmployeeImportRow[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importErrors, setImportErrors] = useState<Record<string, EmployeeRowErrors>>({});
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const draftSaveTimer = useRef<number | null>(null);

  // Step 6: the invitation. Hydrated from the draft, saved back on change.
  const [greeting, setGreeting] = useState("");
  const [greetingSource, setGreetingSource] = useState<GreetingSource>(null);
  const [when, setWhen] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [attested, setAttested] = useState<Partial<Record<AttestationKind, boolean>>>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [doneEnteredAt] = useState(() => Date.now());

  const businessId = businessProfile?.uid ?? localBusinessId ?? undefined;
  const draftQuery = useOnboardingDraft(businessId);
  const saveDraft = useSaveOnboardingDraft();
  const { mutate: saveDraftNow, mutateAsync: saveDraftAsync } = saveDraft;
  const importMembers = useImportSalesTeamMembers();
  const markComplete = useMarkOnboardingComplete();
  const attest = useAttest();
  const suggestGreeting = useSuggestInviteGreeting();
  const ensureCampaign = useEnsureProviderCampaign();
  const createInvites = useCreateAssessmentInvites();
  const serverDraft = draftQuery.data?.draft ?? null;
  const finished = !!businessProfile?.interestedProducts?.length;
  const draftStep = localDraftStep ?? serverDraft?.currentStep ?? null;
  const ready =
    !loadingUser &&
    !profileLoading &&
    (!businessId || draftQuery.isFetched || draftQuery.isError);

  const entitled = hasProductAccess(businessProfile, "intelligence", Date.now());

  const { screen, stepIndex, railItems, goTo, canGoTo } = useOnboardingStep({
    hasUser: !!user,
    hasBusiness: !!businessProfile || !!localBusinessId,
    hasLocalSector: !!localDraft.sector,
    draftStep,
    finished,
    ready,
    devUnlock: import.meta.env.DEV,
  });

  // Once the profile has an explicit vertical, setVertical has already
  // confirmed it - that wins over any local draft, which could otherwise be a
  // stale leftover from another attempt (e.g. a different device in the
  // email-link flow) overriding a value already saved to the business. Before
  // that confirmation (the business exists but a retry hasn't reached
  // setVertical yet, or it failed), the profile's vertical is unset and
  // getBusinessVertical defaults that to "sales", which would silently
  // override a grocery (dvh) choice - so the local draft wins in that window.
  // The draft is cleared only once the organisation step fully succeeds.
  const sector: BusinessVertical | null =
    businessProfile?.vertical != null
      ? getBusinessVertical(businessProfile)
      : (localDraft.sector ?? (businessProfile ? getBusinessVertical(businessProfile) : null));
  const words = useSectorWords(sector);

  useEffect(() => {
    if (rows !== null || !draftQuery.isFetched) return;
    const draft = draftQuery.data?.draft;
    setRows(draft?.employees?.rows ?? []);
    setFileName(draft?.employees?.fileName ?? null);
    setImportSummary(draft?.employees?.importSummary ?? null);
    setTeamNames(draft?.teams?.names ?? {});
    const invitations = draft?.invitations;
    if (invitations?.greeting) setGreeting(invitations.greeting);
    if (invitations?.greetingSource) setGreetingSource(invitations.greetingSource);
    if (invitations?.sendMode) setWhen(invitations.sendMode === "scheduled" ? "later" : "now");
    if (invitations?.scheduledFor) setScheduledAt(invitations.scheduledFor);
    if (invitations?.remindersEnabled != null) setRemindersEnabled(invitations.remindersEnabled);
  }, [rows, draftQuery.isFetched, draftQuery.data]);

  // The roster and teams exist once the business is activated; the invite and
  // done screens read them, the earlier steps never do.
  const rosterQuery = useSalesTeamsMembers(
    entitled && (screen === "invite" || screen === "done") ? businessId : undefined,
  );
  const teamsQuery = useSalesTeams(
    entitled && (screen === "invite" || screen === "done") ? businessId : undefined,
  );
  const recipients = useMemo(
    () =>
      (rosterQuery.data ?? [])
        .filter((member) => !!member.email)
        .map((member) => ({ memberId: member.id, name: member.name, email: member.email! })),
    [rosterQuery.data],
  );
  const submittedBatchId = draftQuery.data?.draft?.invitations?.submittedBatchId ?? null;

  // Anser drafts the greeting the first time the invite step opens empty.
  const greetingRequested = useRef(false);
  useEffect(() => {
    if (screen !== "invite" || !entitled || !businessId || greeting || greetingRequested.current) return;
    greetingRequested.current = true;
    suggestGreeting
      .mutateAsync({ businessId })
      .then((result) => {
        setGreeting((current) => current || result.greeting);
        setGreetingSource(result.source);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, entitled, businessId]);

  const slots = useMemo(
    () => buildInviteSlots(new Date(), { locale: i18n.language === "en" ? "en-GB" : "sv-SE" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screen, i18n.language],
  );

  // The done screen polls while invitations are in flight, for half an hour at most.
  const allTerminalRef = useRef(false);
  const invitesQuery = useAssessmentInvitesList(businessId, 200, {
    enabled: entitled && screen === "done",
    refetchInterval:
      screen === "done" && !allTerminalRef.current && Date.now() - doneEnteredAt < 30 * 60 * 1000
        ? 15_000
        : false,
  });
  const readinessQuery = useTeamReadiness(businessId, { enabled: entitled && screen === "done" });
  const readinessBars = useMemo(
    () =>
      (readinessQuery.data?.teams ?? [])
        .filter((team) => team.invited > 0)
        .map((team) => ({ id: team.salesTeamId, name: team.name, done: team.done, total: team.total })),
    [readinessQuery.data],
  );
  const invites = useMemo(
    () =>
      (invitesQuery.data ?? [])
        .filter((row) => !submittedBatchId || row.inviteBatchId === submittedBatchId)
        .map((row) => ({
          sessionId: row.sessionId,
          memberId: row.memberId,
          memberName: row.memberName,
          salesTeamId: row.salesTeamId,
          boardStatus: row.boardStatus,
        })),
    [invitesQuery.data, submittedBatchId],
  );
  allTerminalRef.current =
    invites.length > 0 && invites.every((i) => TERMINAL_BOARD_STATUSES.includes(i.boardStatus));

  // A business that finished long ago (no draft to resume) belongs in the app.
  useEffect(() => {
    if (!ready || !finished || !draftQuery.isFetched) return;
    if (!serverDraft && !import.meta.env.DEV) navigate("/", { replace: true });
  }, [ready, finished, draftQuery.isFetched, serverDraft, navigate]);

  useEffect(() => {
    trackOnboardingStarted();
    track({
      event: LogEvents.OnboardingStarted.name,
      channel: LogEvents.OnboardingStarted.channel,
    });
  }, []);

  useEffect(() => {
    if (!ready) return;
    trackOnboardingStepViewed(screen, user?.uid, businessId);
    track({
      event: LogEvents.OnboardingStepViewed.name,
      channel: LogEvents.OnboardingStepViewed.channel,
      step_key: screen,
    });
  }, [ready, screen, user?.uid, businessId]);

  // ── Step 1: account by email link ────────────────────────────────────

  const onSignedIn = useCallback(
    (signedIn: User, account: Partial<EmailLinkAccount>) => {
      setUserId(signedIn.uid);
      trackUserRegistrationCompleted(signedIn.uid, account.email ?? signedIn.email ?? "");
      track({
        event: LogEvents.UserRegistrationCompleted.name,
        channel: LogEvents.UserRegistrationCompleted.channel,
      });
      setLocalDraft(readLocalDraft());
      const next = stripEmailLinkParams(searchParams);
      next.set("s", "sector");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const emailLink = useEmailLinkAuth({ onSignedIn });

  const handleSignOut = async () => {
    await signOut();
    clearLocalDraft();
    setLocalDraft({ v: 1 });
    navigate("/sign-in");
  };

  // ── Step 2: sector, kept in the browser until the business exists ────

  const chooseSector = (next: BusinessVertical) => {
    setLocalDraft(writeLocalDraft({ sector: next }));
  };

  // ── Step 3: organisation creates the business and the server draft ───

  const persistOrganisationDraft = (values: LocalDraftOrganisation) => {
    setLocalDraft(writeLocalDraft({ organisation: values }));
  };

  const submitOrganisation = async (values: OrganisationValues) => {
    if (!user) return;
    const chosenSector = sector ?? "sales";
    setSubmitting(true);
    setError(null);
    try {
      let id = businessId;
      if (!id) {
        const name = user.displayName?.trim() || localDraft.account?.name?.trim() || "";
        const email = user.email || localDraft.account?.email || "";
        try {
          const created = await trpcClient.business.create.mutate({
            company: values.unitName,
            // A store number (DVH) is not a legal org number; only a sales
            // company's unit number is the real thing.
            ...(chosenSector === "sales" ? { orgNumber: values.unitNumber } : {}),
            name,
            email,
          });
          id = created.businessId;
          // Persisted before setVertical so a retry after a setVertical
          // failure reuses this business instead of calling create again.
          setLocalBusinessId(id);
        } catch (err) {
          trackBusinessRegistrationError(user.uid, (err as Error)?.message ?? "");
          throw new Error("createBusiness");
        }
      }
      try {
        await trpcClient.business.setVertical.mutate({
          businessId: id,
          vertical: chosenSector,
        });
        trackBusinessRegistrationCompleted(user.uid, id, values.unitName);
        track({
          event: LogEvents.BusinessRegistrationCompleted.name,
          channel: LogEvents.BusinessRegistrationCompleted.channel,
          business_id: id,
        });
      } catch (err) {
        trackBusinessRegistrationError(user.uid, (err as Error)?.message ?? "");
        throw new Error("createBusiness");
      }
      await trpcClient.onboarding.saveOrganisation.mutate({
        businessId: id,
        unitName: values.unitName,
        unitNumber: values.unitNumber,
        headcount: values.headcount,
        ...(chosenSector === "dvh"
          ? { chain: values.choice as "ica_nara" }
          : { salesType: values.choice as "field" }),
        ...(localDraft.account?.mobile ? { mobile: localDraft.account.mobile } : {}),
      });
      trackOnboardingStepCompleted("organisation", user.uid, id);
      setLocalDraftStep("employees");
      setProfileCreated?.(true);
      await refreshProfile();
      await queryClient.invalidateQueries({ queryKey: [["onboarding"]] });
      clearLocalDraft();
      setLocalDraft({ v: 1 });
      setSearchParams({ s: "employees" });
    } catch (err) {
      setError(
        (err as Error)?.message === "createBusiness"
          ? t("organisation.errors.createBusiness")
          : t("organisation.errors.save"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Steps 4-5: employees and teams ───────────────────────────────────

  const scheduleDraftSave = useCallback(
    (patch: OnboardingDraftPatch, delay: number) => {
      if (!businessId) return;
      if (draftSaveTimer.current) window.clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = window.setTimeout(() => {
        draftSaveTimer.current = null;
        saveDraftNow({ businessId, patch });
      }, delay);
    },
    [businessId, saveDraftNow],
  );

  const employeesPatch = (nextRows: EmployeeImportRow[], nextFileName: string | null) => ({
    employees: {
      fileName: nextFileName,
      rows: nextRows,
      importedAt: draftQuery.data?.draft?.employees?.importedAt ?? null,
      importSummary,
    },
  });

  const onRowsChange = (nextRows: EmployeeImportRow[], nextFileName: string | null) => {
    setRows(nextRows);
    setFileName(nextFileName);
    setImportErrors({});
    scheduleDraftSave(employeesPatch(nextRows, nextFileName), 1000);
  };

  const onRenameTeam = (key: string, name: string) => {
    const next = { ...teamNames, [key]: name };
    setTeamNames(next);
    scheduleDraftSave({ teams: { names: next } }, 600);
  };

  const employeesNext = async () => {
    if (!businessId || rows === null) return;
    if (draftSaveTimer.current) window.clearTimeout(draftSaveTimer.current);
    setSubmitting(true);
    setEmployeesError(null);
    try {
      await saveDraftAsync({
        businessId,
        patch: {
          ...employeesPatch(rows, fileName),
          teams: { names: teamNames },
          currentStep: "teams",
          completedSteps: ["employees"],
        },
      });
      trackOnboardingStepCompleted("employees", user?.uid, businessId);
      setLocalDraftStep("teams");
      setSearchParams({ s: "teams" });
    } catch {
      setEmployeesError(t("employees.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const teamsNext = async () => {
    if (!businessId || rows === null || !user) return;
    if (draftSaveTimer.current) window.clearTimeout(draftSaveTimer.current);
    setSubmitting(true);
    setTeamsError(null);
    try {
      if (rows.length > 0) {
        const result = await importMembers.mutateAsync({
          businessId,
          rows,
          teamNames,
          parentTeamId: draftQuery.data?.organisation?.storeTeamId ?? null,
          clearOnboardingDraftRows: true,
          fileName,
        });
        if (result.invalid > 0) {
          const errors: Record<string, EmployeeRowErrors> = {};
          for (const row of result.results) {
            if (row.status === "invalid" && row.errors) errors[row.id] = row.errors;
          }
          setImportErrors(errors);
          setTeamsError(t("teams.invalidRows", { n: result.invalid }));
          setSearchParams({ s: "employees" });
          return;
        }
        setImportSummary({
          created: result.created,
          updated: result.updated,
          invalid: result.invalid,
          teamsCreated: result.teamsCreated,
        });
        setRows([]);
        setFileName(null);
      }
      await markComplete.mutateAsync({ businessId, flow: "v2" });
      await saveDraftAsync({
        businessId,
        patch: { currentStep: "invite", completedSteps: ["employees", "teams"] },
      });
      trackOnboardingStepCompleted("teams", user.uid, businessId);
      setLocalDraftStep("invite");
      setProfileCreated?.(true);
      await refreshProfile();
      setSearchParams({ s: "invite" });
    } catch {
      setTeamsError(t("teams.importError"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 6: the invitation ───────────────────────────────────────────

  const required = REQUIRED_ATTESTATIONS[sector ?? "sales"];

  const persistInvitationDraft = (patch: Partial<{
    greeting: string;
    greetingSource: GreetingSource;
    when: "now" | "later";
    scheduledAt: string | null;
    remindersEnabled: boolean;
  }>) => {
    const next = {
      greeting: patch.greeting ?? greeting,
      greetingSource: patch.greetingSource ?? greetingSource,
      when: patch.when ?? when,
      scheduledAt: patch.scheduledAt === undefined ? scheduledAt : patch.scheduledAt,
      remindersEnabled: patch.remindersEnabled ?? remindersEnabled,
    };
    scheduleDraftSave(
      {
        invitations: {
          greeting: next.greeting || null,
          greetingSource: next.greetingSource,
          sendMode: next.when === "later" ? "scheduled" : "now",
          scheduledFor: next.when === "later" ? next.scheduledAt : null,
          remindersEnabled: next.remindersEnabled,
        },
      },
      600,
    );
  };

  const sendInvites = async () => {
    if (!businessId || !user) return;
    setSubmitting(true);
    setSendError(null);
    try {
      const kinds = required.filter((kind) => attested[kind]);
      if (kinds.length > 0) await attest.mutateAsync({ businessId, kinds });
      try {
        await ensureCampaign.mutateAsync({ businessId, providerId: DEFAULT_ASSESSMENT_PROVIDER_ID });
      } catch {
        setSendError(t("invite.errors.campaign"));
        return;
      }
      const result = await createInvites.mutateAsync({
        businessId,
        providerId: DEFAULT_ASSESSMENT_PROVIDER_ID,
        memberIds: recipients.map((r) => r.memberId),
        message: greeting.trim() || undefined,
        scheduledAt: when === "later" && scheduledAt ? scheduledAt : undefined,
        reminders: remindersEnabled,
        unitName: draftQuery.data?.organisation?.unitName,
      });
      await saveDraftAsync({
        businessId,
        patch: {
          currentStep: "done",
          completedSteps: ["invite"],
          invitations: {
            greeting: greeting.trim() || null,
            greetingSource,
            sendMode: result.scheduledFor ? "scheduled" : "now",
            scheduledFor: result.scheduledFor,
            remindersEnabled,
            submittedBatchId: result.batchId,
          },
        },
      });
      trackOnboardingStepCompleted("invite", user.uid, businessId);
      setLocalDraftStep("done");
      setSearchParams({ s: "done" });
    } catch {
      setSendError(t("invite.errors.send"));
    } finally {
      setSubmitting(false);
    }
  };

  const doneMode: "sent" | "scheduled" =
    draftQuery.data?.draft?.invitations?.sendMode === "scheduled" ? "scheduled" : "sent";
  const doneScheduledFor = draftQuery.data?.draft?.invitations?.scheduledFor ?? null;

  // ── Chrome ───────────────────────────────────────────────────────────

  const stepLabel =
    screen === "welcome"
      ? t("meta.approxTime")
      : screen === "done"
        ? t("meta.done")
        : t("meta.stepOf", { current: stepIndex + 1, total: ONBOARDING_STEPS.length });

  const autosave = useMemo<AutosaveState>(() => {
    if (!businessId) return { kind: "local" };
    if (saveDraft.isPending) return { kind: "saving" };
    if (saveDraft.isError) {
      return {
        kind: "error",
        retry: () => {
          if (saveDraft.variables) saveDraft.mutate(saveDraft.variables);
        },
      };
    }
    if (saveDraft.isSuccess) return { kind: "saved", at: new Date(saveDraft.submittedAt) };
    return { kind: "idle" };
  }, [businessId, saveDraft]);

  const tip = t(`tips.${screen}`, { ...words, defaultValue: "" });

  const menuUser = user
    ? {
        name: user.displayName?.trim() || localDraft.account?.name?.trim() || "",
        email: user.email || localDraft.account?.email || "",
      }
    : undefined;

  const backTo = (target: OnboardingScreen) => (canGoTo(target) ? () => goTo(target) : undefined);

  if (!ready) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-g-bg-app">
        <Loader2 className="h-8 w-8 animate-spin text-g-ink-400" aria-label={t("meta.saving")} />
      </div>
    );
  }

  return (
    <>
      <OnboardingShell
        screen={screen}
        railItems={railItems}
        onRailClick={(key) => goTo(key)}
        stepLabel={stepLabel}
        autosave={autosave}
        tip={tip}
        user={menuUser}
        onSignOut={() => void handleSignOut()}
      >
        {screen === "welcome" ? <WelcomeScreen onStart={() => goTo("account")} /> : null}

        {screen === "account" ? (
          <AccountStep
            phase={emailLink.phase}
            email={emailLink.email}
            error={emailLink.error}
            resendAvailableIn={emailLink.resendAvailableIn}
            initial={localDraft.account ?? {}}
            onSend={(values) => void emailLink.sendLink(values)}
            onResend={() => void emailLink.resend()}
            onConfirmEmail={(email, fallbackAccount) =>
              void emailLink.confirmEmail(email, fallbackAccount)
            }
            onBackToForm={emailLink.backToForm}
            signedInAs={user ? { name: menuUser?.name ?? "", email: menuUser?.email ?? "" } : null}
            onContinue={() => goTo(localDraft.sector ? "organisation" : "sector")}
            onSignOut={() => void handleSignOut()}
          />
        ) : null}

        {screen === "sector" ? (
          <SectorStep
            value={sector}
            onChange={chooseSector}
            onNext={() => goTo("organisation")}
            onBack={backTo("account")}
          />
        ) : null}

        {screen === "organisation" ? (
          <OrganisationStep
            key={sector ?? "sales"}
            sector={sector ?? "sales"}
            words={words}
            initial={{
              unitName: localDraft.organisation?.unitName ?? draftQuery.data?.organisation?.unitName ?? businessProfile?.business,
              unitNumber: localDraft.organisation?.unitNumber ?? draftQuery.data?.organisation?.unitNumber ?? businessProfile?.orgNumber,
              headcount:
                localDraft.organisation?.headcount ??
                (draftQuery.data?.organisation?.headcount != null
                  ? String(draftQuery.data.organisation.headcount)
                  : undefined),
              choice:
                localDraft.organisation?.choice ??
                draftQuery.data?.organisation?.chain ??
                draftQuery.data?.organisation?.salesType,
            }}
            onDraftChange={businessId ? undefined : persistOrganisationDraft}
            onSubmit={(values) => void submitOrganisation(values)}
            submitting={submitting}
            error={error}
            onBack={backTo("sector")}
          />
        ) : null}

        {screen === "employees" ? (
          <EmployeesStep
            words={words}
            rows={rows ?? []}
            fileName={fileName}
            onRowsChange={onRowsChange}
            importSummary={importSummary}
            importErrors={importErrors}
            onNext={() => void employeesNext()}
            onBack={backTo("organisation")}
            saving={submitting}
            error={employeesError}
          />
        ) : null}

        {screen === "teams" ? (
          <TeamsStep
            words={words}
            rows={rows ?? []}
            teamNames={teamNames}
            onRename={onRenameTeam}
            importSummary={importSummary}
            onNext={() => void teamsNext()}
            onBack={backTo("employees")}
            importing={submitting}
            error={teamsError}
          />
        ) : null}

        {screen === "invite" && !entitled ? (
          <InviteWaitingStep
            employeesCount={(importSummary?.created ?? 0) + (importSummary?.updated ?? 0)}
            teamsCount={importSummary?.teamsCreated ?? 0}
            onGoToApp={() => navigate("/")}
          />
        ) : null}

        {screen === "invite" && entitled ? (
          <InviteStep
            words={words}
            recipients={recipients}
            teamsCount={teamsQuery.data?.length ?? 0}
            fromName={menuUser?.name || menuUser?.email || ""}
            unitName={draftQuery.data?.organisation?.unitName ?? businessProfile?.business ?? ""}
            greeting={greeting}
            greetingSource={greetingSource}
            greetingLoading={suggestGreeting.isPending}
            onGreetingChange={(text) => {
              setGreeting(text);
              setGreetingSource("manual");
              persistInvitationDraft({ greeting: text, greetingSource: "manual" });
            }}
            when={when}
            onWhenChange={(next) => {
              setWhen(next);
              persistInvitationDraft({ when: next });
            }}
            slots={slots}
            scheduledAt={slots.some((s) => s.iso === scheduledAt) ? scheduledAt : null}
            onScheduledAtChange={(iso) => {
              setScheduledAt(iso);
              persistInvitationDraft({ scheduledAt: iso });
            }}
            reminders={remindersEnabled}
            onRemindersChange={(on) => {
              setRemindersEnabled(on);
              persistInvitationDraft({ remindersEnabled: on });
            }}
            required={required}
            attested={attested}
            onAttestedChange={(kind, checked) => setAttested((prev) => ({ ...prev, [kind]: checked }))}
            onSend={() => void sendInvites()}
            sending={submitting}
            error={sendError}
            onBack={finished ? undefined : backTo("teams")}
          />
        ) : null}

        {screen === "done" ? (
          <DoneScreen
            mode={doneMode}
            scheduledLabel={
              doneScheduledFor
                ? formatSlotLabel(new Date(doneScheduledFor), i18n.language === "en" ? "en-GB" : "sv-SE", DEFAULT_TIME_ZONE)
                : null
            }
            invites={invites}
            teams={(teamsQuery.data ?? []).map((team) => ({ id: team.id, name: team.name }))}
            remindersEnabled={draftQuery.data?.draft?.invitations?.remindersEnabled ?? true}
            readiness={readinessBars}
            loading={invitesQuery.isLoading}
            error={invitesQuery.isError}
            onRetry={() => void invitesQuery.refetch()}
            onGoToApp={() => navigate("/")}
          />
        ) : null}
      </OnboardingShell>
      <DevThemeSwitch
        steps={["welcome", ...ONBOARDING_STEPS, "done"] as const}
        activeStep={screen}
        onJump={(target) => setSearchParams({ s: target })}
      />
    </>
  );
}
